import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import {
  canApprovePurchaseOrder,
  canCancelPurchaseOrder,
  canClosePurchaseOrder,
  canReceivePurchaseOrder,
  canReopenPurchaseOrder,
  canSendPurchaseOrder,
} from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { fetchLocationsForDropdown } from "../../shared/locationDropdowns";
import { readDefaultLocationFromStorage } from "../../shared/defaultLocationStorage";
import { buildPurchaseOrderPayload } from "../../purchaseOrders/purchaseOrderPayload";
import {
  handlePoApiResult,
  isApiSuccess,
  isUnauthorized,
} from "../../purchaseOrders/purchaseOrderApi";
import {
  createEmptyLineItem,
  createInitialPurchaseOrderFormState,
  mapDetailResponseToForm,
  mapDetailItemToFormRow,
  summarizePurchaseOrderLines,
  unitOfMeasureFromAssemblyRaw,
  unitCostFromAssemblyRaw,
  validatePurchaseOrder,
} from "../../purchaseOrders/purchaseOrderFormUtils";
import type {
  AssemblyItemDropdownOption,
  AssemblyItemDropdownRaw,
  DropdownOption,
  PurchaseOrderDetailResponse,
  PurchaseOrderFormLineItem,
  PurchaseOrderFormState,
  PurchaseOrderLineSummary,
  PurchaseOrderLineItemMutationResponse,
  PurchaseOrderReceivingInvoice,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
} from "../../purchaseOrders/purchaseOrderTypes";
import { purchaseOrderIdFromResponse } from "../../purchaseOrders/purchaseOrderTypes";

type AddSnackbarFn = (snackbar: SnackbarData) => void;
type LogoutFn = () => void;

type SupplierDropdownOption = DropdownOption;
type LocationDropdownOption = DropdownOption;

interface EmptyModalState {
  open: boolean;
  childComponent: ReactNode;
}

export type LineSaveStatus = "idle" | "saving" | "saved" | "error";

export interface CreatePurchaseOrderViewModelState {
  purchaseOrder: PurchaseOrderFormState;
  setPurchaseOrderField: (
    field: keyof PurchaseOrderFormState,
    value: unknown,
  ) => void;
  onSupplierChange: (option: SupplierDropdownOption | null) => Promise<void>;
  onLocationChange: (option: DropdownOption | null) => void;
  lineItems: PurchaseOrderFormLineItem[];
  lineItemsWithTotals: PurchaseOrderFormLineItem[];
  lineSummary: PurchaseOrderLineSummary;
  summary: PurchaseOrderSummary | null;
  receivingInvoices: PurchaseOrderReceivingInvoice[];
  addLineItem: () => void;
  removeLineItem: (tempId: number | string) => Promise<void>;
  updateLineItem: (
    tempId: number | string,
    patch: Partial<PurchaseOrderFormLineItem>,
  ) => void;
  saveLineItem: (tempId: number | string) => Promise<boolean>;
  lineSaveStatuses: Record<string, LineSaveStatus>;
  onLineItemAssemblySelect: (
    tempId: number | string,
    option: AssemblyItemDropdownOption | null,
  ) => void;
  supplierOptions: SupplierDropdownOption[];
  locationOptions: LocationDropdownOption[];
  assemblyItemOptions: AssemblyItemDropdownOption[];
  loading: boolean;
  saving: boolean;
  actionLoading: boolean;
  saveChanges: () => Promise<boolean>;
  createAndApprove: () => Promise<boolean>;
  approve: () => Promise<boolean>;
  send: () => Promise<boolean>;
  cancel: () => Promise<boolean>;
  closePo: () => Promise<boolean>;
  reopen: () => Promise<boolean>;
  init: (routePurchaseOrderId?: string) => Promise<void>;
  emptyModal: EmptyModalState;
  setEmptyModelOpen: Dispatch<SetStateAction<EmptyModalState>>;
  navigateDestination: string | null;
  showErrorModal: (message: string, title?: string) => void;
  purchaseOrderId: number | null;
  poStatus: PurchaseOrderStatus | string;
  poNumber: string | null;
  canApprove: boolean;
  canSend: boolean;
  canCancel: boolean;
  canClose: boolean;
  canReopen: boolean;
  canReceive: boolean;
}

export function useCreatePurchaseOrderViewModel(
  logout: LogoutFn,
  addSnackbar: AddSnackbarFn,
): CreatePurchaseOrderViewModelState {
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(null);
  const [poStatus, setPoStatus] = useState<PurchaseOrderStatus | string>(
    "draft",
  );
  const [poNumber, setPoNumber] = useState<string | null>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderFormState>(
    () => createInitialPurchaseOrderFormState(),
  );
  const [lineItems, setLineItems] = useState<PurchaseOrderFormLineItem[]>([]);
  const [summary, setSummary] = useState<PurchaseOrderSummary | null>(null);
  const [receivingInvoices, setReceivingInvoices] = useState<
    PurchaseOrderReceivingInvoice[]
  >([]);
  const [supplierOptions, setSupplierOptions] = useState<
    SupplierDropdownOption[]
  >([]);
  const [locationOptions, setLocationOptions] = useState<
    LocationDropdownOption[]
  >([]);
  const [assemblyItemOptions, setAssemblyItemOptions] = useState<
    AssemblyItemDropdownOption[]
  >([]);
  const [lineSaveStatuses, setLineSaveStatuses] = useState<
    Record<string, LineSaveStatus>
  >({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [navigateDestination, setNavigateDestination] = useState<string | null>(
    null,
  );
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const purchaseOrderRef = useRef(purchaseOrder);
  const lineItemsRef = useRef(lineItems);
  const purchaseOrderIdRef = useRef(purchaseOrderId);
  const lineSaveQueuesRef = useRef<Record<string, Promise<boolean>>>({});
  const savedStatusTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const draftCreatePromiseRef = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    purchaseOrderRef.current = purchaseOrder;
  }, [purchaseOrder]);

  useEffect(() => {
    lineItemsRef.current = lineItems;
  }, [lineItems]);

  useEffect(() => {
    purchaseOrderIdRef.current = purchaseOrderId;
  }, [purchaseOrderId]);

  useEffect(
    () => () => {
      Object.values(savedStatusTimersRef.current).forEach(clearTimeout);
    },
    [],
  );

  const hasReceivedAmount = Number(summary?.received_amount ?? 0) > 0;

  const lineItemsWithTotals = lineItems;

  const lineSummary = useMemo(
    () => summarizePurchaseOrderLines(lineItemsWithTotals),
    [lineItemsWithTotals],
  );

  const showErrorModal = (message: string, title = "Error") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={message}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getSuppliers = async function () {
    const callResult = await makeGetApiCall("manufacturersForDropdown");
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not fetch suppliers.",
      onSuccess: (data) => {
        const record = data as { suppliers?: SupplierDropdownOption[] };
        const list =
          record.suppliers || (data as SupplierDropdownOption[]) || [];
        setSupplierOptions(
          list.map((s) => ({
            value:
              (s as { value?: number; id?: number }).value ??
              (s as { id?: number }).id!,
            label:
              (s as { label?: string; name?: string }).label ??
              (s as { name?: string }).name ??
              "",
          })),
        );
      },
    });
  };

  const getLocations = async function () {
    const callResult = await fetchLocationsForDropdown();
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not fetch locations.",
      onSuccess: (data) => {
        setLocationOptions((data as LocationDropdownOption[]) || []);
      },
    });
  };

  const getAllAssemblyItems = async function (
    supplierId: number | string | null = null,
  ) {
    const payload = supplierId ? { supplier_id: supplierId } : {};
    const callResult = await makePostApiCall("allAssemblyItems", payload);
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not load items.",
      onSuccess: (data) => {
        const items =
          (data as { assembly_items?: AssemblyItemDropdownRaw[] })
            .assembly_items ?? [];
        setAssemblyItemOptions(
          items.map((item) => ({
            value: item.id,
            label: item.product_name ?? "",
            raw: item,
          })),
        );
      },
    });
  };

  const applyLoadedPurchaseOrder = (data: PurchaseOrderDetailResponse) => {
    const loaded = mapDetailResponseToForm(data);
    setPurchaseOrderId(loaded.purchaseOrderId);
    setPoStatus(loaded.poStatus);
    setPoNumber(loaded.poNumber);
    setSummary(loaded.summary);
    setReceivingInvoices(loaded.receivingInvoices);
    setPurchaseOrder(loaded.purchaseOrder);
    setLineItems(loaded.lineItems);
    return loaded;
  };

  const loadPurchaseOrder = async function (id: number | string) {
    const callResult = await makePostApiCall(`purchaseOrders/${id}`, {});

    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not load purchase order.",
      onSuccess: async (data) => {
        const loaded = applyLoadedPurchaseOrder(
          data as PurchaseOrderDetailResponse,
        );
        return getAllAssemblyItems(loaded.purchaseOrder.supplier_id);
      },
    });
  };

  const init = async function (routePurchaseOrderId?: string) {
    setLoading(true);
    setNavigateDestination(null);

    const suppliersOk = await getSuppliers();
    if (!suppliersOk) {
      setLoading(false);
      return;
    }

    await getLocations();

    if (routePurchaseOrderId) {
      await loadPurchaseOrder(routePurchaseOrderId);
      setLoading(false);
      return;
    }

    setPurchaseOrderId(null);
    setPoStatus("draft");
    setPoNumber(null);
    setSummary(null);
    setReceivingInvoices([]);
    setPurchaseOrder(
      createInitialPurchaseOrderFormState(readDefaultLocationFromStorage()),
    );
    setLineItems([]);
    await getAllAssemblyItems();
    setLoading(false);
  };

  const runAction = async (endpoint: string, successMessage: string) => {
    if (!purchaseOrderId) return false;

    setActionLoading(true);
    const callResult = await makePostApiCall(
      `purchaseOrders/${purchaseOrderId}/${endpoint}`,
      {},
    );
    setActionLoading(false);

    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Action failed.",
      onSuccess: async (data) => {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            (data as { message?: string })?.message || successMessage,
            SnackbarType.Success,
          ),
        );
        await loadPurchaseOrder(purchaseOrderId);
      },
    });
  };

  const setPurchaseOrderField = (
    field: keyof PurchaseOrderFormState,
    value: unknown,
  ) => {
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onLocationChange = (option: DropdownOption | null) => {
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({
      ...prev,
      location: option ?? null,
    }));
  };

  const onSupplierChange = async (option: SupplierDropdownOption | null) => {
    const supplierId = option?.value ?? null;
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({
      ...prev,
      supplier_id: supplierId,
      supplier_label: option?.label ?? "",
    }));
    await getAllAssemblyItems(supplierId);
  };

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      createEmptyLineItem(generateUniqueNumber()),
    ]);
  };

  const removeLineItem = async (tempId: number | string) => {
    const row = lineItems.find((r) => r.temp_id === tempId);
    if (!row) return;

    if (row.line_locked) {
      showErrorModal("Lines with received quantity cannot be removed.");
      return;
    }

    if (!row.id) {
      setLineItems((prev) => prev.filter((r) => r.temp_id !== tempId));
      return;
    }

    const callResult = await makePostApiCall(
      `deletePurchaseOrderItem/${row.id}`,
      {},
    );

    await handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not delete purchase order item.",
      onSuccess: async (data) => {
        setLineItems((prev) => prev.filter((r) => r.temp_id !== tempId));
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            (data as { message?: string })?.message ||
              "Purchase order item deleted.",
            SnackbarType.Success,
          ),
        );
        if (purchaseOrderId != null) {
          await loadPurchaseOrder(purchaseOrderId);
        }
      },
    });
  };

  const updateLineItem = (
    tempId: number | string,
    patch: Partial<PurchaseOrderFormLineItem>,
  ) => {
    const next = lineItemsRef.current.map((row) =>
      row.temp_id === tempId ? { ...row, ...patch } : row,
    );
    lineItemsRef.current = next;
    setLineItems(next);
  };

  const setLineSaveStatus = (
    tempId: number | string,
    status: LineSaveStatus,
  ) => {
    const key = String(tempId);
    if (savedStatusTimersRef.current[key]) {
      clearTimeout(savedStatusTimersRef.current[key]);
      delete savedStatusTimersRef.current[key];
    }
    setLineSaveStatuses((current) => ({ ...current, [key]: status }));

    if (status === "saved") {
      savedStatusTimersRef.current[key] = setTimeout(() => {
        setLineSaveStatuses((current) => ({ ...current, [key]: "idle" }));
        delete savedStatusTimersRef.current[key];
      }, 1400);
    }
  };

  const lineItemPayload = (row: PurchaseOrderFormLineItem) => ({
    assembly_item_id: row.assembly_item_id,
    supplier_plu: row.supplier_plu || null,
    description: row.description || null,
    ordered_amount: Number(row.ordered_quantity),
    unit_of_measure: row.unit_of_measure,
    unit_cost: row.unit_cost === "" ? null : Number(row.unit_cost),
    lot_number: row.expected_lot_number || null,
    notes: row.notes || null,
  });

  const createDraftForInlineSave = async () => {
    if (draftCreatePromiseRef.current) return draftCreatePromiseRef.current;

    const validation = validatePurchaseOrder(
      purchaseOrderRef.current,
      lineItemsRef.current,
    );
    if (!validation.valid) return false;

    const operation = (async () => {
      const payload = buildPurchaseOrderPayload(
        purchaseOrderRef.current,
        lineItemsRef.current,
        supplierOptions,
      );
      const callResult = await makePostApiCall("createPurchaseOrder", payload);

      if (!isApiSuccess(callResult)) {
        if (isUnauthorized(callResult)) logout();
        return false;
      }

      const data = callResult.data as PurchaseOrderDetailResponse;
      const id = purchaseOrderIdFromResponse(data);
      if (id == null) return false;

      purchaseOrderIdRef.current = id;
      setPurchaseOrderId(id);
      if (data.purchase_order) {
        const loaded = applyLoadedPurchaseOrder(data);
        lineItemsRef.current = loaded.lineItems;
      }
      setNavigateDestination(`/purchase-orders/${id}`);
      return true;
    })();

    draftCreatePromiseRef.current = operation;
    operation.finally(() => {
      if (draftCreatePromiseRef.current === operation) {
        draftCreatePromiseRef.current = null;
      }
    });
    return operation;
  };

  const persistLineItem = async (tempId: number | string) => {
    let row = lineItemsRef.current.find((item) => item.temp_id === tempId);
    if (!row || !row.assembly_item_id || Number(row.ordered_quantity) <= 0) {
      return true;
    }

    if (purchaseOrderIdRef.current == null) {
      return createDraftForInlineSave();
    }

    const endpoint = row.id
      ? `purchaseOrders/${purchaseOrderIdRef.current}/items/${row.id}`
      : `purchaseOrders/${purchaseOrderIdRef.current}/items`;
    const callResult = row.id
      ? await makePutApiCall(endpoint, lineItemPayload(row))
      : await makePostApiCall(endpoint, lineItemPayload(row));

    if (!isApiSuccess(callResult)) {
      if (isUnauthorized(callResult)) logout();
      else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            (callResult.data as { message?: string })?.message ||
              "Could not save purchase order item.",
            SnackbarType.Error,
          ),
        );
      }
      return false;
    }

    const data = callResult.data as PurchaseOrderLineItemMutationResponse;
    const savedRow = mapDetailItemToFormRow(data.item);
    const next = lineItemsRef.current.map((item) =>
      item.temp_id === tempId ? { ...savedRow, temp_id: tempId } : item,
    );
    lineItemsRef.current = next;
    setLineItems(next);
    setSummary(data.summary);
    return true;
  };

  const saveLineItem = (tempId: number | string) => {
    const key = String(tempId);
    const row = lineItemsRef.current.find((item) => item.temp_id === tempId);
    if (!row || !row.assembly_item_id || Number(row.ordered_quantity) <= 0) {
      setLineSaveStatus(tempId, "idle");
      return Promise.resolve(true);
    }

    if (
      purchaseOrderIdRef.current == null &&
      !validatePurchaseOrder(purchaseOrderRef.current, lineItemsRef.current)
        .valid
    ) {
      setLineSaveStatus(tempId, "idle");
      return Promise.resolve(true);
    }

    setLineSaveStatus(tempId, "saving");
    const previous = lineSaveQueuesRef.current[key] ?? Promise.resolve(true);
    const task = previous
      .catch(() => false)
      .then(() => persistLineItem(tempId));
    lineSaveQueuesRef.current[key] = task;
    task.then((success) => {
      if (lineSaveQueuesRef.current[key] !== task) return;
      delete lineSaveQueuesRef.current[key];
      setLineSaveStatus(tempId, success ? "saved" : "error");
    });
    return task;
  };

  const onLineItemAssemblySelect = (
    tempId: number | string,
    option: AssemblyItemDropdownOption | null,
  ) => {
    const raw = option?.raw;
    updateLineItem(tempId, {
      assembly_item_id: option?.value ?? null,
      item_name: raw?.name || raw?.product_name || option?.label || "",
      supplier_plu: raw?.plu ?? raw?.supplier_plu ?? "",
      description: raw?.description || raw?.name || raw?.product_name || "",
      unit_of_measure: unitOfMeasureFromAssemblyRaw(raw),
      unit_cost: unitCostFromAssemblyRaw(raw),
    });
    if (option?.value != null) void saveLineItem(tempId);
  };

  const persistPurchaseOrder = async function ({
    approveAfterSave = false,
  }: {
    approveAfterSave?: boolean;
  }) {
    const validation = validatePurchaseOrder(purchaseOrder, lineItems);
    if (!validation.valid) {
      showErrorModal(validation.message);
      return false;
    }

    setSaving(true);
    try {
      const payload = buildPurchaseOrderPayload(
        purchaseOrder,
        lineItems,
        supplierOptions,
      );

      const wasCreate = purchaseOrderId == null;
      let id: number | null = purchaseOrderId;
      let callResult;

      if (id != null) {
        callResult = await makePutApiCall(`purchaseOrders/${id}`, payload);
      } else {
        callResult = await makePostApiCall("createPurchaseOrder", payload);
      }

      if (
        !(await handlePoApiResult(callResult, {
          logout,
          onError: showErrorModal,
          errorMessage: "Could not save purchase order.",
        }))
      ) {
        return false;
      }

      if (id == null) {
        id = purchaseOrderIdFromResponse(
          callResult.data as Parameters<typeof purchaseOrderIdFromResponse>[0],
        );
      }

      if (approveAfterSave && id != null) {
        const approveResult = await makePostApiCall(
          `purchaseOrders/${id}/approve`,
          {},
        );

        if (
          !(await handlePoApiResult(approveResult, {
            logout,
            onError: (message) => {
              showErrorModal(
                message ||
                  "Purchase order was saved but could not be approved.",
              );
            },
            errorMessage: "Purchase order was saved but could not be approved.",
            onSuccess: async (data) => {
              addSnackbar(
                new SnackbarData(
                  generateUniqueNumber(),
                  (data as { message?: string })?.message ||
                    "Purchase order created and approved.",
                  SnackbarType.Success,
                ),
              );

              if (wasCreate && id != null) {
                setNavigateDestination(`/purchase-orders/${id}`);
              } else if (id != null) {
                await loadPurchaseOrder(id);
              }
            },
          }))
        ) {
          if (wasCreate && id != null) {
            setNavigateDestination(`/purchase-orders/${id}`);
          } else if (id != null) {
            await loadPurchaseOrder(id);
          }
          return false;
        }

        return true;
      }

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          (callResult.data as { message?: string })?.message ||
            (wasCreate
              ? "Purchase order draft saved."
              : "Purchase order saved."),
          SnackbarType.Success,
        ),
      );

      if (wasCreate && id != null) {
        setNavigateDestination(`/purchase-orders/${id}`);
      } else if (id != null) {
        await loadPurchaseOrder(id);
      }

      return true;
    } finally {
      setSaving(false);
    }
  };

  const saveChanges = () => persistPurchaseOrder({ approveAfterSave: false });
  const createAndApprove = () =>
    persistPurchaseOrder({ approveAfterSave: true });
  const approve = () => runAction("approve", "Purchase order approved.");
  const send = () => runAction("send", "Purchase order sent to supplier.");
  const cancel = () => runAction("cancel", "Purchase order cancelled.");
  const closePo = () => runAction("close", "Purchase order closed.");
  const reopen = () => runAction("reopen", "Purchase order reopened.");

  return {
    purchaseOrder,
    setPurchaseOrderField,
    onSupplierChange,
    onLocationChange,
    lineItems,
    lineItemsWithTotals,
    lineSummary,
    summary,
    receivingInvoices,
    addLineItem,
    removeLineItem,
    updateLineItem,
    saveLineItem,
    lineSaveStatuses,
    onLineItemAssemblySelect,
    supplierOptions,
    locationOptions,
    assemblyItemOptions,
    loading,
    saving,
    actionLoading,
    saveChanges,
    createAndApprove,
    approve,
    send,
    cancel,
    closePo,
    reopen,
    init,
    emptyModal,
    setEmptyModelOpen,
    navigateDestination,
    showErrorModal,
    purchaseOrderId,
    poStatus,
    poNumber,
    canApprove: canApprovePurchaseOrder(poStatus),
    canSend: canSendPurchaseOrder(poStatus),
    canCancel: canCancelPurchaseOrder(poStatus) && !hasReceivedAmount,
    canClose: canClosePurchaseOrder(poStatus),
    canReopen: canReopenPurchaseOrder(poStatus),
    canReceive: canReceivePurchaseOrder(poStatus),
  };
}

/** @deprecated Use `useCreatePurchaseOrderViewModel` */
export const CreatePurchaseOrderViewModel = useCreatePurchaseOrderViewModel;
